"use client";
import {
  FC,
  HTMLAttributes,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Script from "next/script";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useLocale, useTranslations } from "next-intl";
import { IconSkeleton } from "@/components/loadingSkeleton/loadingSkeleton";
import { GOOGLEMAP_URL } from "@/utils/constants";
import { useNativeActions } from "@/utils/useNativeActions";

export interface GoogleMapsProps {
  lat: number;
  lng: number;
  height: any;
  width: any;
}

type Address = {
  address1: string;
  address2?: string;
  address3?: string;
  city: string;
  country: string;
  state: string;
  zip: string;
};

const GOOGLE_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";
const BASE_URL = GOOGLEMAP_URL;

const isLocalhost = () =>
  typeof globalThis !== "undefined" &&
  globalThis.location.hostname === "localhost";

function createAddressString({ address1, city, state, zip }: Address) {
  return [address1, city, state, zip].join();
}

const MockMap: FC<
  HTMLAttributes<HTMLDivElement> & {
    address: Address;
    height: any;
    width: any;
  }
> = memo(function MockMap({ height, width, ...props }) {
  const t = useTranslations("GoogleMap");
  return (
    <div
      {...props}
      className="relative overflow-hidden aspect-square h-96 w-full bg-gray-200 flex items-center justify-center text-gray-500"
      style={{ height: height, width: width }}
      data-testid={applyDataTestId(pom.merchantFooter.map)}
    >
      {t("mockGoogleMap")}
    </div>
  );
});

export const GoogleMap: FC<
  HTMLAttributes<HTMLDivElement> & {
    address: Address;
    height: any;
    width: any;
  }
> = memo(function GoogleMap({ address, height, width, ...props }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isLocal, setIsLocal] = useState(false);
  const { isCmaApp, isAndroid, openExternalLink } = useNativeActions();

  const isAndroidCma = isCmaApp && isAndroid;

  const locale = useLocale();

  const scriptSrc = useMemo(() => {
    const params = new URLSearchParams({
      key: GOOGLE_API_KEY,
      libraries: "places,marker",
      language: locale,
    });
    return `${BASE_URL}?${params.toString()}`;
  }, [locale]);

  const handleMapRedirect = () => {
    const query = encodeURIComponent(createAddressString(address));
    const safeUrl = `https://maps.google.com/?q=...${query}`;
    openExternalLink(safeUrl);
  };

  useEffect(() => {
    setIsLocal(isLocalhost());
    if (globalThis.google?.maps) {
      setIsScriptLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isLocal) return; // Skip effect if on localhost

    if (!isScriptLoaded || !mapRef.current) return;

    const geoCoder = new google.maps.Geocoder();

    geoCoder.geocode(
      { address: createAddressString(address) },
      (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK || !results) {
          console.error(`Geocode failed with status: ${status}`);
          return;
        }

        const { location } = results[0].geometry;

        const map = new google.maps.Map(mapRef.current!, {
          center: location,
          zoom: 15,
          mapId: "DEMO_MAP_ID",
          clickableIcons: !isAndroidCma,
        });

        if (google.maps.marker?.AdvancedMarkerElement) {
          const marker = new google.maps.marker.AdvancedMarkerElement({
            position: location,
            map,
          });
          marker.map = map;

          // Intercept click on Android
          if (isAndroidCma) {
            marker.addListener("click", handleMapRedirect);
          }
        } else {
          console.warn("AdvancedMarkerElement is not available.");
        }
      }
    );
  }, [address, isScriptLoaded, isAndroidCma]);

  if (isLocal) {
    return (
      <MockMap address={address} height={height} width={width} {...props} />
    );
  }

  return (
    <div {...props} className={`${props.className || ""}`}>
      <Script src={scriptSrc} onLoad={() => setIsScriptLoaded(true)} />

      {!isScriptLoaded && (
        <IconSkeleton
          width={width}
          height={height}
          styles={{ borderRadius: "0" }}
        />
      )}

      <div
        className="relative z-0"
        style={{ height, width, display: isScriptLoaded ? "block" : "none" }}
        data-testid={applyDataTestId(pom.merchantFooter.map)}
      >
        <div className="h-full w-full" ref={mapRef} />

        {isScriptLoaded && isAndroidCma && (
          <>
            <button
              type="button"
              className="absolute bottom-0 left-0 h-14 w-28 z-10 cursor-pointer bg-transparent border-none p-0 m-0 outline-none"
              onClick={handleMapRedirect}
              aria-label="Open in Google Maps"
            />
            <button
              type="button"
              className="absolute bottom-0 right-0 h-10 w-56 z-10 cursor-pointer bg-transparent border-none p-0 m-0 outline-none"
              onClick={handleMapRedirect}
              aria-label="Open in Google Maps"
            />
          </>
        )}
      </div>
    </div>
  );
});
